import { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateAdmin, useGetAdmins, useUpdateAdminPassword, useDeleteAdmin } from '../backend/hooks';

function AdminList() {
  const { data, isLoading, error } = useGetAdmins();
  const { mutate: updatePassword } = useUpdateAdminPassword();
  const { mutate: deleteAdmin } = useDeleteAdmin();
  const admins = data?.admins || [];

  const handlePassword = (admin) => {
    Swal.fire({
      title: 'Update Password',
      html: `
        <p class="text-sm text-gray-500 mb-2">For <strong>${String(admin.user_name).replace(/</g, '&lt;')}</strong></p>
        <input id="new-admin-password" type="password" class="swal2-input w-full" placeholder="New password (min 6 characters)" autocomplete="new-password" />
        <input id="confirm-admin-password" type="password" class="swal2-input w-full" placeholder="Confirm new password" autocomplete="new-password" />`,
      showCancelButton: true,
      confirmButtonText: 'Update Password',
      confirmButtonColor: '#0F67FE',
      focusConfirm: false,
      preConfirm: () => {
        const password = document.getElementById('new-admin-password')?.value || '';
        const confirm = document.getElementById('confirm-admin-password')?.value || '';
        if (password.length < 6) {
          Swal.showValidationMessage('Password must be at least 6 characters');
          return false;
        }
        if (password !== confirm) {
          Swal.showValidationMessage('Passwords do not match');
          return false;
        }
        return password;
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      updatePassword(
        { id: admin.id, password: result.value },
        {
          onSuccess: () => toast.success(`Password updated for ${admin.user_name}`, { position: 'bottom-right' }),
          onError: (err) => toast.error(err.message, { position: 'bottom-right' }),
        }
      );
    });
  };

  const handleDelete = (admin) => {
    Swal.fire({
      title: 'Delete admin?',
      text: `"${admin.user_name}" will no longer be able to log in to the dashboard.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteAdmin(admin.id, {
        onSuccess: () => toast.success('Admin deleted', { position: 'bottom-right' }),
        onError: (err) => toast.error(err.message, { position: 'bottom-right' }),
      });
    });
  };

  return (
    <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/15 overflow-hidden">
      <div className="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-low flex justify-between items-center">
        <h2 className="text-lg font-bold text-on-surface">Administrators</h2>
        <span className="text-xs font-bold text-on-surface-variant">{admins.length} total</span>
      </div>
      {isLoading ? (
        <div className="p-6 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
        </div>
      ) : error ? (
        <p className="p-6 text-error">Error: {error.message}</p>
      ) : (
        <ul className="divide-y divide-surface-container-high">
          {admins.map((admin) => (
            <li key={admin.id} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center uppercase shrink-0">
                  {String(admin.user_name || '?').charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-on-surface truncate">{admin.user_name}</p>
                  {admin.is_current && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5 rounded-full">You</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => handlePassword(admin)} className="p-2 rounded-lg text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors" title="Update password">
                  <span className="material-symbols-outlined">key</span>
                </button>
                <button
                  onClick={() => handleDelete(admin)}
                  disabled={admin.is_current || admins.length <= 1}
                  className="p-2 rounded-lg text-error hover:bg-error/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title={admin.is_current ? 'You cannot delete your own account' : admins.length <= 1 ? 'At least one admin must remain' : 'Delete admin'}
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Settings() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  
  const { mutate: createAdmin, isPending: isSubmitting } = useCreateAdmin();
  const queryClient = useQueryClient();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Payload: only username (email) and password (backend requirement)
    const payload = {
      username: formData.email,
      password: formData.password,
    };

    createAdmin(payload, {
      onSuccess: () => {
        toast.success('Admin created successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        });
        queryClient.invalidateQueries({ queryKey: ['admins'] });
        // Reset form
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
        });
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to create admin', {
          position: 'bottom-right',
          autoClose: 4000,
        });
      },
    });
  };

  return (
    <div className="space-y-8 p-3">
      <div>
        <h1 className="text-3xl font-extrabold text-on-surface tracking-tight">Settings</h1>
        <p className="text-on-surface-variant mt-2">Manage system settings and administrator accounts.</p>
      </div>

      <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/15 overflow-hidden">
        <div className="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-low">
          <h2 className="text-lg font-bold text-on-surface">Create New Administrator</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email (used as username) */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-on-surface-variant">
                Email Address / Username <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full bg-surface-container-highest border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary transition-all ${
                  errors.email ? 'ring-2 ring-error' : ''
                }`}
                placeholder="admin@example.com"
              />
              {errors.email && <p className="text-error text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-on-surface-variant">
                Password <span className="text-error">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full bg-surface-container-highest border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary transition-all ${
                  errors.password ? 'ring-2 ring-error' : ''
                }`}
                placeholder="••••••"
              />
              {errors.password && <p className="text-error text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-on-surface-variant">
                Confirm Password <span className="text-error">*</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full bg-surface-container-highest border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary transition-all ${
                  errors.confirmPassword ? 'ring-2 ring-error' : ''
                }`}
                placeholder="••••••"
              />
              {errors.confirmPassword && <p className="text-error text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
          </div>

          <div className="pt-4 border-t border-outline-variant/10 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-primary text-on-primary rounded-xl font-bold shadow-md hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                  Creating...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">add_circle</span>
                  Create Admin
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <AdminList />
    </div>
  );
}